/**
 * Insight Library (INSIGHT-LIB) — the MC-level curated rollup of the
 * knowledge registers across all 8 workshops. A read-and-curate view (not a
 * fourth copy of the data): findings grouped by domain with a tag filter,
 * opportunities faceted by their `opportunity:<tag>` namespace, and
 * recommendations with an inline drafting field that writes back via the
 * register's `update()` (the manual surface `AI-REC` later fills). Exposes a
 * client-side CSV export of the aggregated registers (plan1 A5). Clicking a
 * row routes to its workshop and opens the Workshop Log.
 */

import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Download, Lightbulb, ReceiptText, Sparkles, Telescope, X } from "lucide-react";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { cn } from "@/lib/utils";
import { WORKSHOPS, type Workshop } from "@/content/workshops";
import { FIELD_CLASS, severityAccent, statusAccent } from "@/features/workshop/modals/log-controls.utils";
import { AddButton } from "@/features/workshop/modals/log-rows";
import { TagFilter } from "@/components/tags/TagFilter";
import { collectTags, matchesTags } from "@/lib/tags";
import {
  FINDING_DOMAINS,
  FINDING_DOMAIN_LABELS,
  isNsTag,
  stripNs,
  TAG_NS_OPPORTUNITY,
} from "@/lib/taxonomy";
import { hydrateAllRegisters } from "@/features/missionControl/useProgramRollup";
import { useFindings } from "@/stores/useFindings";
import { useOpportunities } from "@/stores/useOpportunities";
import { useRecommendations, type Recommendation } from "@/stores/useRecommendations";
import { useWorkshopModals } from "@/features/workshop/modals/useWorkshopModals";
import { useAiDraft, buildRecPrompt } from "@/features/xaes-analyst/ai-draft";

type InsightLibraryModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The launching workshop (drives same-workshop navigation). */
  workshopId: string;
};

type LibTab = "findings" | "opportunities" | "recommendations";

const LIB_TABS: ReadonlyArray<{ key: LibTab; label: string }> = [
  { key: "findings", label: "Findings" },
  { key: "opportunities", label: "Opportunities" },
  { key: "recommendations", label: "Recommendations" },
];

type WithWs<T> = T & { __ws: Workshop["id"] };

function flatten<T extends { id: string }>(byWs: Record<string, T[]>): WithWs<T>[] {
  return WORKSHOPS.flatMap((w) =>
    (byWs[w.id] ?? []).map((r) => ({ ...r, __ws: w.id }) as WithWs<T>),
  );
}

/* ── CSV export ────────────────────────────────────────────────────── */

function csvCell(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function downloadCsv(filename: string, rows: string[][]): void {
  const csv = rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function InsightLibraryModal({ open, onOpenChange, workshopId }: InsightLibraryModalProps) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const openModal = useWorkshopModals((s) => s.open);

  const findingsByWs = useFindings((s) => s.entriesByWorkshop);
  const opportunitiesByWs = useOpportunities((s) => s.entriesByWorkshop);
  const recommendationsByWs = useRecommendations((s) => s.entriesByWorkshop);
  const updateRecommendation = useRecommendations((s) => s.update);

  const [tab, setTab] = useState<LibTab>("findings");
  const [activeTags, setActiveTags] = useState<string[]>([]);

  useEffect(() => {
    if (open) void hydrateAllRegisters();
  }, [open]);

  const findings = useMemo(() => flatten(findingsByWs), [findingsByWs]);
  const opportunities = useMemo(() => flatten(opportunitiesByWs), [opportunitiesByWs]);
  const recommendations = useMemo(() => flatten(recommendationsByWs), [recommendationsByWs]);

  const findingTags = useMemo(() => collectTags(findings), [findings]);
  // Opportunity tags faceted on their namespace (shown de-namespaced).
  const opportunityTags = useMemo(
    () =>
      collectTags(opportunities).filter((t) => isNsTag(TAG_NS_OPPORTUNITY, t)),
    [opportunities],
  );

  const filteredFindings = useMemo(
    () => findings.filter((f) => matchesTags(f.tags, activeTags)),
    [findings, activeTags],
  );
  const findingsByDomain = useMemo(
    () =>
      FINDING_DOMAINS.map((d) => ({
        domain: d,
        items: filteredFindings.filter((f) => f.domain === d),
      })).filter((g) => g.items.length > 0),
    [filteredFindings],
  );

  const filteredOpportunities = useMemo(
    () => opportunities.filter((o) => matchesTags(o.tags, activeTags)),
    [opportunities, activeTags],
  );

  const recommendationTags = useMemo(() => collectTags(recommendations), [recommendations]);
  const filteredRecommendations = useMemo(
    () => recommendations.filter((r) => matchesTags(r.tags, activeTags)),
    [recommendations, activeTags],
  );

  function navTo(wsId: string): void {
    if (wsId !== workshopId) {
      navigate(`/workshop/${wsId}`, { state: { from: pathname + search } });
    }
    requestAnimationFrame(() => openModal("decision-log"));
  }

  function exportCsv(): void {
    const rows: string[][] = [
      ["workshop", "type", "title", "status", "severity", "domain", "owner", "tags", "detail"],
    ];
    for (const f of findings)
      rows.push([f.__ws, "finding", f.title, f.status, f.severity, f.domain, f.owner, (f.tags ?? []).join("; "), f.detail]);
    for (const o of opportunities)
      rows.push([o.__ws, "opportunity", o.title, o.status, "", "", o.owner, (o.tags ?? []).map((t) => stripNs(TAG_NS_OPPORTUNITY, t)).join("; "), o.detail]);
    for (const r of recommendations)
      rows.push([r.__ws, "recommendation", r.title, r.status, "", "", r.owner, (r.tags ?? []).join("; "), r.body]);
    downloadCsv("xaes-insight-library.csv", rows);
  }

  const totalRows = findings.length + opportunities.length + recommendations.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PanelDialogContent
        className="w-[90vw] max-w-[900px] border-0 bg-transparent p-0 shadow-none"
        style={{ outline: "none" }}
      >
        <DialogTitle className="sr-only">Insight Library</DialogTitle>
        <DialogDescription className="sr-only">
          Curated rollup of findings, opportunities, and recommendations across all workshops.
        </DialogDescription>
        <div className="modal-shell ig-library" style={{ width: "100%", maxHeight: "85vh" }}>
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Sparkles aria-hidden style={{ width: 16, height: 16, color: "var(--accent-true-blue)" }} />
              <span className="modal-title">INSIGHT LIBRARY</span>
              <span
                style={{
                  padding: "1px 8px",
                  background: "rgba(var(--accent-sky-rgb), 0.08)",
                  border: "1px solid rgba(var(--accent-sky-rgb), 0.25)",
                  borderRadius: 10,
                  fontSize: "var(--fs-micro)",
                  color: "var(--accent-sky)",
                  fontWeight: 600,
                  letterSpacing: 1,
                }}
              >
                {findings.length} findings · {opportunities.length} opportunities · {recommendations.length} recs
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                type="button"
                onClick={exportCsv}
                disabled={totalRows === 0}
                className="modal-close-btn inline-flex items-center gap-1.5"
                title="Export the aggregated registers as CSV"
                style={{ opacity: totalRows === 0 ? 0.5 : 1, cursor: totalRows === 0 ? "default" : "pointer" }}
              >
                <Download aria-hidden style={{ width: 12, height: 12 }} />
                CSV
              </button>
              <button type="button" onClick={() => onOpenChange(false)} className="modal-close-btn" aria-label="Close">
                <X aria-hidden style={{ width: 12, height: 12 }} />
                CLOSE
              </button>
            </div>
          </div>

          <div className="dt-tab-bar" role="tablist" aria-label="Insight Library sections">
            {LIB_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                onClick={() => setTab(t.key)}
                className={cn("dt-tab", tab === t.key && "dt-tab-active")}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {tab === "findings" && (
              <>
                {findingTags.length > 0 && (
                  <TagFilter
                    allTags={findingTags}
                    selected={activeTags}
                    onChange={setActiveTags}
                    workshopId={workshopId}
                  />
                )}
                {findingsByDomain.length === 0 && (
                  <p style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)" }}>
                    No findings yet{activeTags.length ? " for the selected tags." : "."}
                  </p>
                )}
                {findingsByDomain.map((group) => (
                  <section key={group.domain}>
                    <GroupHead
                      icon={Telescope}
                      label={FINDING_DOMAIN_LABELS[group.domain]}
                      count={group.items.length}
                    />
                    <ul className="flex flex-col gap-1.5 pt-2">
                      {group.items.map((f) => (
                        <LibRow
                          key={`${f.__ws}-${f.id}`}
                          ws={f.__ws}
                          title={f.title}
                          onClick={() => navTo(f.__ws)}
                          meta={
                            <>
                              <span style={{ color: severityAccent(f.severity), fontWeight: 600 }}>{f.severity}</span>
                              {` · ${f.status}`}
                              {f.owner ? ` · ${f.owner}` : ""}
                            </>
                          }
                        />
                      ))}
                    </ul>
                  </section>
                ))}
              </>
            )}

            {tab === "opportunities" && (
              <>
                {opportunityTags.length > 0 && (
                  <TagFilter
                    allTags={opportunityTags}
                    selected={activeTags}
                    onChange={setActiveTags}
                    workshopId={workshopId}
                    formatLabel={(t) => stripNs(TAG_NS_OPPORTUNITY, t)}
                  />
                )}
                <section>
                  <GroupHead icon={Lightbulb} label="Opportunities" count={filteredOpportunities.length} />
                  <ul className="flex flex-col gap-1.5 pt-2">
                    {filteredOpportunities.length === 0 && (
                      <li style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)" }}>
                        No opportunities yet{activeTags.length ? " for the selected tags." : "."}
                      </li>
                    )}
                    {filteredOpportunities.map((o) => (
                      <LibRow
                        key={`${o.__ws}-${o.id}`}
                        ws={o.__ws}
                        title={o.title}
                        onClick={() => navTo(o.__ws)}
                        meta={
                          <>
                            <span style={{ color: statusAccent(o.status), fontWeight: 600 }}>{o.status}</span>
                            {o.owner ? ` · ${o.owner}` : ""}
                          </>
                        }
                      />
                    ))}
                  </ul>
                </section>
              </>
            )}

            {tab === "recommendations" && (
              <section>
                {recommendationTags.length > 0 && (
                  <div style={{ paddingBottom: 10 }}>
                    <TagFilter
                      allTags={recommendationTags}
                      selected={activeTags}
                      onChange={setActiveTags}
                      workshopId={workshopId}
                    />
                  </div>
                )}
                <GroupHead icon={ReceiptText} label="Recommendations" count={filteredRecommendations.length} />
                <ul className="flex flex-col gap-2 pt-2">
                  {filteredRecommendations.length === 0 && (
                    <li style={{ fontSize: "var(--fs-label)", color: "var(--text-muted)" }}>
                      No recommendations{activeTags.length ? " for the selected tags." : " drafted yet."}
                    </li>
                  )}
                  {filteredRecommendations.map((r) => (
                    <RecCard
                      key={`${r.__ws}-${r.id}`}
                      rec={r}
                      onOpen={() => navTo(r.__ws)}
                      onSaveBody={(body) => updateRecommendation(r.__ws, r.id, { body })}
                    />
                  ))}
                </ul>
              </section>
            )}
          </div>

          <div className="modal-footer">
            <span className="modal-footer-label">XAES Phase Zero</span>
            <span className="modal-footer-sep">|</span>
            <span className="modal-footer-ws">Insight Library · MC rollup</span>
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}

function GroupHead({ icon: Icon, label, count }: { icon: typeof Telescope; label: string; count: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Icon aria-hidden style={{ width: 14, height: 14, color: "var(--accent-true-blue)" }} />
      <span
        style={{
          fontSize: "var(--fs-label)",
          letterSpacing: "1.5px",
          textTransform: "uppercase",
          color: "var(--text-secondary)",
          fontWeight: 700,
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: "var(--fs-micro)", color: "var(--text-muted)", fontWeight: 600 }}>({count})</span>
    </div>
  );
}

function LibRow({
  ws,
  title,
  meta,
  onClick,
}: {
  ws: string;
  title: string;
  meta: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        aria-label={`${title} — open in ${ws}`}
        className="flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left cursor-pointer transition-colors border-[rgba(var(--accent-true-blue-rgb),0.12)] bg-[rgba(var(--accent-true-blue-rgb),0.04)] hover:bg-[rgba(var(--accent-true-blue-rgb),0.10)] hover:border-[rgba(var(--accent-true-blue-rgb),0.30)] focus-visible:outline-none focus-visible:border-[var(--accent-true-blue)]"
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: "var(--fs-base)",
              fontWeight: 600,
              color: "var(--text-primary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </div>
          <div style={{ fontSize: "var(--fs-micro)", color: "var(--text-muted)" }}>
            <span style={{ color: "var(--accent-sky)", letterSpacing: 0.4 }}>{ws}</span> · {meta}
          </div>
        </div>
      </button>
    </li>
  );
}

/** A recommendation with an editable drafting body (writes back on Save). */
function RecCard({
  rec,
  onOpen,
  onSaveBody,
}: {
  rec: Recommendation & { __ws: string };
  onOpen: () => void;
  onSaveBody: (body: string) => void;
}) {
  const [draft, setDraft] = useState(rec.body);
  // Re-sync if the underlying record changes (e.g. AI fill) while open.
  useEffect(() => setDraft(rec.body), [rec.body]);
  const ai = useAiDraft();
  // Stream the AI draft into the editable body (AI-REC).
  useEffect(() => {
    if (ai.status === "streaming" || (ai.status === "idle" && ai.text)) setDraft(ai.text);
  }, [ai.text, ai.status]);
  const dirty = draft !== rec.body;
  const streaming = ai.status === "streaming";

  function draftWithAi(): void {
    void (async () => {
      const { system, prompt } = await buildRecPrompt(rec.__ws as never, rec.title);
      await ai.run(system, prompt);
    })();
  }

  return (
    <li
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: "10px",
        borderRadius: 8,
        background: "rgba(var(--accent-true-blue-rgb), 0.04)",
        border: "1px solid rgba(var(--accent-true-blue-rgb), 0.12)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          onClick={onOpen}
          style={{
            background: "transparent",
            border: 0,
            padding: 0,
            cursor: "pointer",
            fontSize: "var(--fs-base)",
            fontWeight: 600,
            color: "var(--text-primary)",
            textAlign: "left",
          }}
        >
          {rec.title}
        </button>
        <span style={{ fontSize: "var(--fs-micro)", color: statusAccent(rec.status), fontWeight: 600 }}>
          {rec.status}
        </span>
        <span style={{ fontSize: "var(--fs-micro)", color: "var(--accent-sky)", marginLeft: "auto", letterSpacing: 0.4 }}>
          {rec.__ws}
        </span>
      </div>
      <textarea
        aria-label={`Recommendation body: ${rec.title}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={2}
        placeholder="Draft the recommendation…"
        className={cn(FIELD_CLASS, "resize-y")}
      />
      {ai.error && (
        <p style={{ fontSize: "var(--fs-micro)", color: "var(--accent-red)" }}>{ai.error}</p>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button
          type="button"
          onClick={draftWithAi}
          disabled={streaming}
          className="modal-close-btn inline-flex items-center gap-1.5"
          title="Draft this recommendation with AI (editable before you save)"
          style={{ flex: "none", opacity: streaming ? 0.5 : 1, cursor: streaming ? "default" : "pointer" }}
        >
          <Sparkles aria-hidden style={{ width: 12, height: 12 }} />
          {streaming ? "DRAFTING…" : "DRAFT WITH AI"}
        </button>
        <AddButton onClick={() => onSaveBody(draft.trim())} disabled={!dirty || streaming} label="SAVE DRAFT" />
      </div>
    </li>
  );
}
